import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from './auth.service';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { SessionEventService } from '../../../core/services/session-event.service';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptorService implements HttpInterceptor {
  private lastNetworkToastAt = 0;

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private authService: AuthService,
    private sessionEventService: SessionEventService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Only the gateway itself can say the session is over, so only gateway responses are
    // allowed to end it.
    const isGatewayRequest = req.url === environment.loyaltyApiUrl;
    // Never react to a 401 from the logout call itself — that response is
    // expected once the token is already gone, and re-triggering logout()
    // here would recurse: logout POST -> 401 -> logout() -> logout POST -> 401 -> ...
    // On the gateway the logout IS action 8075 on the one /action url, so the path
    // check alone never matched it.
    const isLogoutRequest =
      (isGatewayRequest && req.body?.actioncode === 8075) ||
      req.url.includes('/auth/admin/logout');

    return next.handle(req).pipe(
      tap((event) => {
        // The gateway answers a dead session with HTTP 200 and an envelope, never a 401:
        // { isSuccess:false, code:'ERROR_CODE_ACCESS_DENIED', reason:'INVALID_TOKEN' } for an
        // expired / unreadable token, or code 'TOKEN_REVOKED' once it was logged out. The 401
        // branch below therefore never fired, and an expired admin stayed on the page with
        // every widget failing "Invalid or expired token" instead of going back to sign-in.
        if (
          event instanceof HttpResponse &&
          isGatewayRequest &&
          !isLogoutRequest &&
          this.isDeadSession(event.body) &&
          this.sentCurrentToken(req)
        ) {
          this.endSession();
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (
          isGatewayRequest &&
          !isLogoutRequest &&
          error.status === 401 &&
          error.error?.code === 'ERROR_CODE_UNAUTHORIZED' &&
          this.sentCurrentToken(req)
        ) {
          this.endSession();
        }

        // 503 = backend is in scheduled maintenance / downtime — show the
        // maintenance screen instead of letting the raw error surface. Only the gateway's
        // 503 means that: a third-party service (the AI generator) answering 503 used to
        // throw the whole admin onto the maintenance screen.
        if (isGatewayRequest && error.status === 503 && !this.router.url.startsWith('/maintenance')) {
          this.router.navigate(['/maintenance']);
        }

        // status 0 = the request never reached the server (offline / DNS / CORS / timeout).
        // The error is still rethrown below so each caller can reset its own loading state.
        if (error.status === 0) {
          this.notifyNetworkError();
        }

        return throwError(() => error);
      })
    );
  }

  /** One toast per burst: a page load fires several requests that all fail together. */
  private notifyNetworkError(): void {
    const now = Date.now();
    if (now - this.lastNetworkToastAt < 5000) return;
    this.lastNetworkToastAt = now;
    this.toastr.error('Network error — please check your connection and try again.', 'Network Error');
  }

  /** The gateway's words for "this token no longer opens a session". ERROR_CODE_ACCESS_DENIED
   *  is only a session failure when it carries reason INVALID_TOKEN — the same code is the
   *  end-user login's answer to a wrong password. */
  private isDeadSession(body: any): boolean {
    if (!body || body.isSuccess !== false) return false;
    if (body.code === 'TOKEN_REVOKED' || body.code === 'UNAUTHENTICATED') return true;
    return (
      body.code === 'ERROR_CODE_ACCESS_DENIED' &&
      (body.reason === 'INVALID_TOKEN' || /invalid or expired token/i.test(body.message || ''))
    );
  }

  /** A reply to a request sent with an older token (still in flight while the admin signed in
   *  again, or sent before another tab refreshed the session) says nothing about the current
   *  session, so it must not sign the admin out. Requests without a token never count. */
  private sentCurrentToken(req: HttpRequest<any>): boolean {
    const sent = (req.headers.get('Authorization') || '').replace(/^Bearer\s+/i, '');
    return !!sent && sent === this.authService.currentToken();
  }

  private endSession(): void {
    this.sessionEventService.notifySessionTimeout();
    this.authService.logout();
  }
}
